import React, { useEffect, useRef, useState } from 'react';
import { fy, plural, type TechMini } from './api';
import { CloseIcon } from './icons';
import { fyTrack } from './track';

interface Props {
  /** Saved tech offers, or ones recommended for a problem statement. */
  items: TechMini[];
  maxRecipients: number;
  noteMax: number;
  onUnpick: (id: string) => void;
  onClose: () => void;
  /** After a send: the confirmation to show; the parent clears the selection. */
  onSent: (message: string) => void;
  onCopied: (message: string) => void;
}

const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const splitEmails = (v: string) => v.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall back below */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

const ShareDialog: React.FC<Props> = ({ items, maxRecipients, noteMax, onUnpick, onClose, onSent, onCopied }) => {
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'' | 'send' | 'copy'>('');
  const [err, setErr] = useState('');
  const first = useRef<HTMLInputElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    first.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const n = items.length;
  const title = `Share ${plural(n, 'tech offer', 'tech offers')}`;
  const emails = splitEmails(to);
  // The address still being typed is not flagged until a separator follows it.
  const typing = /[^,;\s]$/.test(to);
  const bad = emails.filter((e, i) => !EMAIL_RE.test(e) && !(typing && i === emails.length - 1));
  const invalid = emails.some(e => !EMAIL_RE.test(e));
  const tooMany = emails.length > maxRecipients;
  const canSend = n > 0 && emails.length > 0 && !invalid && !tooMany && !busy;
  const ids = items.map(t => t.id);

  const send = async () => {
    if (!canSend) return;
    setBusy('send'); setErr('');
    const r = await fy.share(to, note, ids);
    setBusy('');
    if (!r.ok || !r.data) { setErr(r.error); return; }
    fyTrack('foryou_share_send', { count: n, recipients: r.data.recipients, note: note.trim().length > 0 });
    onSent(r.data.message);
  };
  const copy = async () => {
    if (!n || busy) return;
    setBusy('copy'); setErr('');
    const r = await fy.copy(note, ids);
    if (!r.ok || !r.data) { setBusy(''); setErr(r.error); return; }
    const ok = await copyToClipboard(r.data.text);
    setBusy('');
    if (!ok) { setErr('Your browser blocked copying. Please try again.'); return; }
    fyTrack('foryou_share_copy', { count: n });
    onCopied(`Message with ${plural(n, 'tech offer', 'tech offers')} copied — paste it anywhere`);
  };

  return (
    <div className="fyx fy-overlay" onClick={onClose}>
      <div className="fy-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <button type="button" className="fy-icon-btn fy-close" aria-label="Close" onClick={onClose}><CloseIcon /></button>
        <div>
          <div className="fy-kicker">Share in one message</div>
          <h2 className="fy-h2" style={{ marginTop: 4 }}>{title}</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label className="fy-label" htmlFor="fy-share-to">Send to</label>
          <input id="fy-share-to" ref={first} type="text" className="fy-input" value={to} autoComplete="email"
                 onChange={e => setTo(e.target.value)}
                 placeholder="name@company.com — separate emails with commas" />
          {bad.length > 0 && <div className="fy-err">Check: {bad.slice(0, 3).join(', ')}</div>}
          {tooMany && <div className="fy-err">Send to up to {maxRecipients} people at a time.</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label className="fy-label" htmlFor="fy-share-note">
            Add a note <span style={{ fontWeight: 400, color: 'var(--fy-muted)' }}>(optional)</span>
          </label>
          <textarea id="fy-share-note" className="fy-textarea" rows={3} value={note} maxLength={noteMax}
                    onChange={e => setNote(e.target.value)} placeholder="e.g. Shortlist for Thursday's R&D review" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="fy-label">Included in this message</div>
          {items.map(t => (
            <div key={t.id} className="fy-inc">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fy-ink)', overflowWrap: 'anywhere' }}>{t.title}</div>
                <div className="fy-muted">View on Cosmos</div>
              </div>
              <button type="button" className="fy-icon-btn" aria-label={`Remove ${t.title} from message`}
                      onClick={() => { fyTrack('foryou_share_remove_item', { tech_id: t.id }); onUnpick(t.id); }}>
                <CloseIcon size={16} />
              </button>
            </div>
          ))}
        </div>
        {err && <div className="fy-err" role="alert">{err}</div>}
        <div className="fy-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="fy-btn fy-btn-quiet" onClick={copy} disabled={!n || !!busy}>
            {busy === 'copy' ? 'Copying…' : 'Copy message'}
          </button>
          <button type="button" className="fy-btn fy-btn-primary" onClick={send} disabled={!canSend}
                  style={{ minHeight: 46, padding: '0 22px' }}>
            {busy === 'send' ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
